import type { QueryClient, QueryKey } from "@tanstack/react-query";
import {
  useFindAllDeliveryQuery,
  useFindAllOffersQuery,
  useFindDeliveryByIdQuery,
} from "@/libs/generated/hooks";
import type {
  FindAllOffersQuery,
  FindAllOffersQueryVariables,
  FindDeliveryByIdQuery,
} from "@/libs/generated/operations";

const offersQueryRoot = useFindAllOffersQuery.getKey({
  GetAllInput: {
    pagination: {
      page: 0,
      perPage: 1,
    },
  },
})[0];

const deliveryListQueryRoot = useFindAllDeliveryQuery.getKey({
  GetAllInput: {
    pagination: {
      page: 0,
      perPage: 1,
    },
  },
})[0];

type OfferRecord = FindAllOffersQuery["findAllOffers"]["records"][number];

type OfferCacheTarget = {
  deliveryId: number;
  offerId?: number;
};

type OfferCacheSnapshot = {
  offerLists: Array<[QueryKey, FindAllOffersQuery | undefined]>;
  deliveryDetail: FindDeliveryByIdQuery | undefined;
};

export function getOffersByDeliveryQueryVariables(deliveryId: number): FindAllOffersQueryVariables {
  return {
    GetAllInput: {
      filter: {
        deliveryId,
      },
      pagination: {
        page: 0,
        perPage: 100,
      },
      sort: {
        field: "createdAt",
        order: "desc",
      },
    },
  };
}

export function getOffersByDeliveryQueryKey(deliveryId: number) {
  return useFindAllOffersQuery.getKey(getOffersByDeliveryQueryVariables(deliveryId));
}

export function getDeliveryDetailQueryKey(deliveryId: number) {
  return useFindDeliveryByIdQuery.getKey({
    GetByIdInput: { id: deliveryId },
  });
}

export function snapshotOfferCache(
  queryClient: QueryClient,
  target: OfferCacheTarget
): OfferCacheSnapshot {
  return {
    offerLists: getMatchingOfferQueries(queryClient, target),
    deliveryDetail: queryClient.getQueryData<FindDeliveryByIdQuery>(
      getDeliveryDetailQueryKey(target.deliveryId)
    ),
  };
}

export function restoreOfferCache(
  queryClient: QueryClient,
  snapshot: OfferCacheSnapshot,
  deliveryId: number
) {
  for (const [queryKey, data] of snapshot.offerLists) {
    queryClient.setQueryData(queryKey, data);
  }

  queryClient.setQueryData(getDeliveryDetailQueryKey(deliveryId), snapshot.deliveryDetail);
}

export async function cancelOfferQueries(queryClient: QueryClient, deliveryId: number) {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: [offersQueryRoot] }),
    queryClient.cancelQueries({ queryKey: getDeliveryDetailQueryKey(deliveryId) }),
  ]);
}

export async function invalidateOfferRelatedQueries(queryClient: QueryClient, deliveryId: number) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [offersQueryRoot] }),
    queryClient.invalidateQueries({ queryKey: getDeliveryDetailQueryKey(deliveryId) }),
    queryClient.invalidateQueries({ queryKey: [deliveryListQueryRoot] }),
  ]);
}

export function updateOfferLists(
  queryClient: QueryClient,
  target: OfferCacheTarget,
  updater: (offer: OfferRecord) => OfferRecord | undefined
) {
  for (const [queryKey, data] of getMatchingOfferQueries(queryClient, target)) {
    const nextRecords = data.findAllOffers.records
      .map(updater)
      .filter((offer): offer is OfferRecord => Boolean(offer));

    const hasChanged =
      nextRecords.length !== data.findAllOffers.records.length ||
      nextRecords.some((offer, index) => offer !== data.findAllOffers.records[index]);

    if (!hasChanged) {
      continue;
    }

    queryClient.setQueryData<FindAllOffersQuery>(queryKey, {
      ...data,
      findAllOffers: {
        ...data.findAllOffers,
        records: nextRecords,
        total: Math.max(
          0,
          data.findAllOffers.total + (nextRecords.length - data.findAllOffers.records.length)
        ),
      },
    });
  }
}

export function updateDeliveryDetail(
  queryClient: QueryClient,
  deliveryId: number,
  updater: (data: FindDeliveryByIdQuery) => FindDeliveryByIdQuery
) {
  queryClient.setQueryData<FindDeliveryByIdQuery>(
    getDeliveryDetailQueryKey(deliveryId),
    (current) => (current ? updater(current) : current)
  );
}

function getMatchingOfferQueries(queryClient: QueryClient, target: OfferCacheTarget) {
  return queryClient
    .getQueriesData<FindAllOffersQuery>({ queryKey: [offersQueryRoot] })
    .filter(([queryKey, data]) => {
      if (!data) {
        return false;
      }

      const queryDeliveryId = getDeliveryIdFromOfferQueryKey(queryKey);

      if (queryDeliveryId === target.deliveryId) {
        return true;
      }

      return (
        target.offerId !== undefined &&
        data.findAllOffers.records.some((offer) => offer.id === target.offerId)
      );
    });
}

function getDeliveryIdFromOfferQueryKey(queryKey: QueryKey) {
  const variables = queryKey[1] as FindAllOffersQueryVariables | undefined;
  const filter = variables?.GetAllInput?.filter as { deliveryId?: unknown } | undefined;

  return typeof filter?.deliveryId === "number" ? filter.deliveryId : undefined;
}
